import { useRouter, useRoute } from 'vue-router';
import { useTabBarStore, useRouterStore } from '@/store';
import { REDIRECT_ROUTE_NAME, NOT_FOUND_ROUTE_NAME } from '@/router/constants';

export default function useTabbar() {
  const tabbar = useTabBarStore();
  const router = useRouter();
  const route = useRoute();
  const closeSelfAndPush = (name: string, query?: Recordable) => {
    tabbar
      .deleteTagByFullPath(route.fullPath as string)
      .then(() => {
        router.push({ name, query });
      })
      .catch(() => ({}));
  };
  /**
   * 用于关闭当前Tag并返回到fromTag，如果fromTag已被关闭则打开name路由
   * fromTag表示当前页面的来源Tag
   * @param name 备用的跳转理由
   * @param query
   */
  const closeSelfAndBackFrom = (name: string, query?: Recordable) => {
    const { fullPath } = route;
    const { fromTag } = tabbar.getTagByFullPush(fullPath) ?? {};
    tabbar
      .deleteTagByFullPath(fullPath as string)
      .then(() => {
        if (fromTag) {
          router.push({ ...fromTag });
        } else {
          router.push({ name, query });
        }
      })
      .catch(() => ({}));
  };
  /**
   * 用于保存并新增的场景，pushName不传时将刷新当前url（包含参数）的路由
   * @param pushName 前往的路由名称，可以是自己
   * @param query 只有存在pushName才会带上query参数
   */
  const reloadSelf = async (pushName?: string, query?: Recordable) => {
    const { name, fullPath } = route;
    if (pushName) {
      // 删除当前tag，防止pushName的tag和当前tag不一致会导致多出一个tag
      tabbar
        .deleteTagByFullPath(fullPath)
        .then(() => {
          router.replace({
            name: REDIRECT_ROUTE_NAME,
            params: {
              name: String(pushName),
            },
            query,
          });
        })
        .catch(() => ({}));
    } else {
      // 重新加载当前tag
      tabbar.deleteCacheTag(route as any);
      await router.push({
        name: REDIRECT_ROUTE_NAME,
        params: {
          path: fullPath,
        },
      });
      tabbar.addCacheTag(route as any);
    }
  };
  /**
   * 用于当关闭tag时需要校验或者提醒用户的操作
   * @param callback 通过执行callback中的done来决定是否关闭tag（必须执行done）
   */
  const setCloseCallback = (
    callback: (done: (d?: boolean) => void) => void
  ) => {
    const { fullPath } = route;
    const tag = tabbar.getTagByFullPush(fullPath);
    if (tag) {
      // callback必须执行done
      tag.closeTagCallback = async (done: (d?: boolean) => void) => {
        if (route.fullPath === tag.fullPath) {
          // 关闭当前tag
          return callback(done);
        }
        // 关闭时先push到该tag
        await router.push({ ...tag });
        return callback(done);
      };
    }
  };
  /**
   * 用于打开一个新标签，如果存在该路径的标签则会先关闭旧的再打开新的
   * @param routerTemp 原push的路由参数
   */
  const reopenTag = (routerTemp: Recordable) => {
    const pushRouter = router.resolve(routerTemp);
    tabbar
      .deleteTagByFullPath(pushRouter.fullPath)
      .then(() => {
        router.push(routerTemp);
      })
      .catch(() => ({}));
  };
  // 关闭当前tag并且跳转到无权限页面，用于没有查看权限接口的重定向
  const handleNoPermission = () => {
    tabbar
      .deleteTagByFullPath(window.location.hash.slice(1) as string)
      .then(() => {
        const { globalRouter } = useRouterStore();
        globalRouter?.push({ name: NOT_FOUND_ROUTE_NAME, query: {} });
      });
  };
  return {
    closeSelfAndPush,
    closeSelfAndBackFrom,
    reloadSelf,
    setCloseCallback,
    reopenTag,
    handleNoPermission,
  };
}
